import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { ResourceData } from './resource.state';

/*
 * A staged resource draft. Extends the catalog's `ResourceData` with the full set
 * of editor-owned attributes the future resource editor writes and the save
 * cascade reads. `value`/`version` are always present (the persistence payload);
 * `displayName`/`metadata` are optional editor extras; `file` is the lazily
 * selected artifact carried in memory until persistence uploads it.
 */
export interface ResourceDraftData extends ResourceData {
  version: number;
  value: string;
  displayName?: Record<string, string>;
  metadata?: Record<string, unknown>;
  file?: File;
}

/*
 * Local edit applied to a resource that DOES exist in the database. We carry both
 * halves so the listing flow can hide the stale DB row and place the edited
 * version at its new sorted position, and so the total count remains exact even
 * when the edit moves the resource in/out of the active search match.
 */
export interface ResourceDraftOverride {
  original: ResourceDraftData;
  override: ResourceDraftData;
}

interface StagedResourcesStateModel {
  /*
   * Resources created in the UI but not yet persisted. Source of truth for the
   * save cascade — the catalog's items adapter is just a per-page snapshot that
   * may surface some of these.
   */
  pendingResources: ResourceDraftData[];

  /*
   * Local edits to persisted resources, keyed by id. Until persisted, the listing
   * flow shows the override in place of the DB row sharing this id.
   */
  resourceOverrides: Record<string, ResourceDraftOverride>;

  /*
   * Exceptions from the last save pass, keyed by the in-memory id of the row that
   * failed (placeholder id for pending drafts, `original.id` for overrides). A
   * cascade batch can fail several rows at once, so this is a map rather than a
   * single slot. Mirrors `LabelState.saveExceptions`.
   */
  saveExceptions: Record<string, Error>;

  saving: boolean;
}

const INITIAL_STATE: StagedResourcesStateModel = {
  pendingResources: [],
  resourceOverrides: {},
  saveExceptions: {},
  saving: false,
};

/**
 * Shared resource staging ledger. One instance is provided by the outermost
 * consumer and shared down the (future) dialog stack: drafts survive a manager
 * unmount, and nested managers see the same ledger. This is the future source of
 * truth the save cascade reads (mirroring how `LabelState.pendingLabels$` /
 * `labelOverrides$` feed `SaveProtocolCascadeEffects`).
 *
 * Deliberately NOT part of `resourceStoreProviders` / `resourceManagerProviders`
 * (same contract as `AssignedResourcesState`): a per-manager copy would be torn
 * down with each manager and could not survive the dialog stack.
 */
@Injectable()
export class StagedResourcesState extends ComponentStore<StagedResourcesStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly pendingResources$ = this.select((state) => state.pendingResources);
  public readonly resourceOverrides$ = this.select((state) => state.resourceOverrides);
  public readonly saveExceptions$ = this.select((state) => state.saveExceptions);
  public readonly saving$ = this.select((state) => state.saving);

  // ============================================================================
  // pendingResources updaters
  // ============================================================================

  /*
   * Upsert-by-id with a no-op guard: the manager facade re-applies the staged set
   * on every tree emission, so an unchanged entry must return the same state
   * reference (a fresh array would fire a spurious selector emit).
   */
  public readonly addPendingResource = this.updater((state, item: ResourceDraftData) => {
    const normalized: ResourceDraftData = { ...item, pending: true };
    const existing = state.pendingResources.find((p) => p.id === normalized.id);
    if (existing && this.sameResourceData(existing, normalized)) return state;
    const without = state.pendingResources.filter((p) => p.id !== normalized.id);
    return { ...state, pendingResources: [...without, normalized] };
  });

  public readonly removePendingResource = this.updater((state, id: string) => ({
    ...state,
    pendingResources: state.pendingResources.filter((p) => p.id !== id),
  }));

  public readonly clearPendingResources = this.updater((state) => ({ ...state, pendingResources: [] }));

  // ============================================================================
  // resourceOverrides updaters
  // ============================================================================

  /*
   * Same no-op guard rationale as addPendingResource. The override half is
   * normalized onto the original's id (mirror of LabelState.setLabelOverride).
   */
  public readonly setResourceOverride = this.updater((state, entry: ResourceDraftOverride) => {
    const id = entry.original.id;
    const normalizedOverride: ResourceDraftData = { ...entry.override, id, pending: undefined };
    const existing = state.resourceOverrides[id];
    if (
      existing &&
      this.sameResourceData(existing.original, entry.original) &&
      this.sameResourceData(existing.override, normalizedOverride)
    ) {
      return state;
    }
    return {
      ...state,
      resourceOverrides: {
        ...state.resourceOverrides,
        [id]: { original: entry.original, override: normalizedOverride },
      },
    };
  });

  public readonly removeResourceOverride = this.updater((state, id: string) => {
    if (!(id in state.resourceOverrides)) return state;
    const resourceOverrides = { ...state.resourceOverrides };
    delete resourceOverrides[id];
    return { ...state, resourceOverrides };
  });

  public readonly clearResourceOverrides = this.updater((state) => ({ ...state, resourceOverrides: {} }));

  // ============================================================================
  // Tree sync
  // ============================================================================

  /*
   * Non-destructive sync from the staged containment tree. The tree only knows a
   * node's display data (name + nested count); every other draft attribute is
   * owned by the editor and lives only here. So:
   *  - when the entry is ABSENT, create it with safe defaults (the editor fills
   *    the rest later);
   *  - when the entry is PRESENT, merge ONLY name + nestedResourcesCount and
   *    preserve everything else — clobbering with tree defaults would wipe the
   *    editor's `value`/`version`/`displayName`/... on the next tree emission.
   * The referential no-op guard keeps repeated tree emissions from churning the
   * slices (each emission re-applies the whole staged set).
   */
  public readonly stageFromTree = this.updater(
    (state, payload: { id: string; name: string; nestedResourcesCount: number; mode: 'save' | 'update' }) => {
      if (payload.mode === 'save') {
        const existing = state.pendingResources.find((p) => p.id === payload.id);
        const merged: ResourceDraftData = existing
          ? { ...existing, name: payload.name, nestedResourcesCount: payload.nestedResourcesCount }
          : {
              id: payload.id,
              name: payload.name,
              type: '',
              nestedResourcesCount: payload.nestedResourcesCount,
              version: 1,
              value: '',
              pending: true,
            };
        if (existing && this.sameResourceData(existing, merged)) return state;
        const without = state.pendingResources.filter((p) => p.id !== payload.id);
        return { ...state, pendingResources: [...without, merged] };
      }

      const existing = state.resourceOverrides[payload.id];
      if (existing) {
        const override: ResourceDraftData = {
          ...existing.override,
          name: payload.name,
          nestedResourcesCount: payload.nestedResourcesCount,
        };
        if (this.sameResourceData(existing.override, override)) return state;
        return {
          ...state,
          resourceOverrides: {
            ...state.resourceOverrides,
            [payload.id]: { original: existing.original, override },
          },
        };
      }
      const seed: ResourceDraftData = {
        id: payload.id,
        name: payload.name,
        type: '',
        nestedResourcesCount: payload.nestedResourcesCount,
        version: 1,
        value: '',
      };
      return {
        ...state,
        resourceOverrides: { ...state.resourceOverrides, [payload.id]: { original: seed, override: seed } },
      };
    }
  );

  // ============================================================================
  // saveExceptions / saving updaters
  // ============================================================================

  // Record one row's save failure keyed by its id. Does NOT touch `saving` —
  // bulk callers (the cascade) flip `saving` once after the whole batch settles.
  public readonly recordSaveException = this.updater((state, entry: { id: string; error: Error }) => ({
    ...state,
    saveExceptions: { ...state.saveExceptions, [entry.id]: entry.error },
  }));

  // Clear all recorded save failures. No-op when already empty so a fresh `{}`
  // reference can't fire a spurious selector emit (which under fakeAsync can
  // leave a pending timer in the queue).
  public readonly clearSaveExceptions = this.updater((state) =>
    Object.keys(state.saveExceptions).length === 0 ? state : { ...state, saveExceptions: {} }
  );

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  private sameResourceData(a: ResourceDraftData, b: ResourceDraftData): boolean {
    return (
      a.id === b.id &&
      a.name === b.name &&
      a.type === b.type &&
      a.description === b.description &&
      a.nestedResourcesCount === b.nestedResourcesCount &&
      a.pending === b.pending &&
      a.version === b.version &&
      a.value === b.value &&
      a.file === b.file &&
      JSON.stringify(a.displayName) === JSON.stringify(b.displayName) &&
      JSON.stringify(a.metadata) === JSON.stringify(b.metadata)
    );
  }
}
